"use client";

import { useState, useEffect, useCallback, useMemo } from "react";

export interface BaseImageResponsiveProps {
  alt?: string;
  width?: number;
  height?: number;
  className?: string;
  fill?: boolean;
  priority?: boolean;
  style?: React.CSSProperties;
  onClick?: () => void;
  onClickHandler?: () => void;
  containerClassName?: string;
}

export interface ScreenSizeInfo {
  width: number;
  height: number;
  pixelRatio: number;
  viewportWidth: number;
}

export interface ImageSizeConfig {
  baseUrl: string;
  imageTag: string;
  fallbackSrc: string;
  useOriginal: boolean;
  setUseOriginal: (value: boolean) => void;
}

export const useScreenSizeDetection = () => {
  const [screenSize, setScreenSize] = useState<ScreenSizeInfo>({ 
    width: 0, 
    height: 0, 
    pixelRatio: 1,
    viewportWidth: 0 
  });

  // Memoized screen size update function to prevent unnecessary re-renders
  const updateScreenSize = useCallback(() => {
    setScreenSize({
      width: window.innerWidth,
      height: window.innerHeight,
      pixelRatio: window.devicePixelRatio || 1,
      viewportWidth: window.visualViewport?.width || window.innerWidth,
    });
  }, []);

  // Detect screen size, pixel ratio, and viewport on mount and resize
  useEffect(() => {
    // Set initial size immediately if possible
    if (typeof window !== 'undefined') {
      updateScreenSize();
    }

    // Add event listener for resize with debouncing
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateScreenSize, 100);
    };

    // Add event listeners
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', updateScreenSize);

    // Cleanup
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', updateScreenSize);
    };
  }, [updateScreenSize]);

  return screenSize;
};

// Helper function to get intelligent initial size guess
export const getIntelligentInitialSize = (): string => {
  if (typeof window === 'undefined') return 'medium';
  
  // Check if we can get viewport size immediately
  if (window.visualViewport?.width) {
    const width = window.visualViewport.width;
    if (width <= 480) return 'small';
    if (width <= 768) return 'medium';
    if (width <= 1024) return 'large';
    return 'xlarge';
  }
  
  if (window.innerWidth) {
    const width = window.innerWidth;
    if (width <= 480) return 'small';
    if (width <= 768) return 'medium';
    if (width <= 1024) return 'large';
    return 'xlarge';
  }
  
  // Check user agent for mobile hints
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /mobile|android|iphone|ipad|phone/i.test(userAgent);
  
  if (isMobile) {
    // Check for high-DPI mobile devices
    const pixelRatio = window.devicePixelRatio || 1;
    if (pixelRatio >= 2) return 'medium'; // High-DPI mobile
    return 'small'; // Regular mobile
  }
  
  // Default to medium for better performance
  return 'medium';
};

export const useImageSizeSelection = (
  screenSize: ScreenSizeInfo,
  effectiveWidth: number | undefined,
  baseUrl: string,
  imageTag: string
) => {
  return useMemo(() => {
    // If we don't have screen size yet, use intelligent initial guess
    if (!screenSize.width && !effectiveWidth) {
      const initialSize = getIntelligentInitialSize();
      return { 
        sizeFolder: initialSize, 
        imageSrc: `${baseUrl}/${initialSize}/${imageTag}.webp`,
        highDpiAdjustment: false,
        primarySize: initialSize,
        isEstimated: true
      };
    }

    // If we have effective width but no screen size, use width-based estimation
    if (effectiveWidth && !screenSize.width) {
      let estimatedSize = "large";
      if (effectiveWidth <= 480) estimatedSize = "small";
      else if (effectiveWidth <= 768) estimatedSize = "medium";
      else if (effectiveWidth <= 1024) estimatedSize = "large";
      else estimatedSize = "xlarge";
      
      return { 
        sizeFolder: estimatedSize, 
        imageSrc: `${baseUrl}/${estimatedSize}/${imageTag}.webp`,
        highDpiAdjustment: false,
        primarySize: estimatedSize,
        isEstimated: true
      };
    }

    // If we have screen size, use the full logic
    if (effectiveWidth) {
      // For high-DPI displays, we need to balance between quality and performance
      // Use a hybrid approach: consider both physical and logical dimensions
      const physicalWidth = effectiveWidth * screenSize.pixelRatio;
      const logicalWidth = effectiveWidth;
      
      // Primary decision based on logical width (what the user actually sees)
      let primarySize = "large";
      if (logicalWidth <= 480) {
        // Mobile phones - use small for better performance
        primarySize = "small";
      } else if (logicalWidth <= 768) {
        // Large phones/small tablets - use medium
        primarySize = "medium";
      } else if (logicalWidth <= 1024) {
        // Tablets - use large
        primarySize = "large";
      } else if (logicalWidth <= 1440) {
        // Laptops - use large
        primarySize = "large";
      } else {
        // Large monitors - use xlarge
        primarySize = "xlarge";
      }
      
      // Start with primary size
      let sizeFolder = primarySize;
      
      // Adjust for very high-DPI displays (3x+) to ensure quality
      let highDpiAdjustment = false;
      if (screenSize.pixelRatio >= 3 && logicalWidth > 480) {
        // For very high-DPI displays, bump up one size category
        highDpiAdjustment = true;
        if (sizeFolder === "small") sizeFolder = "medium";
        else if (sizeFolder === "medium") sizeFolder = "large";
        else if (sizeFolder === "large") sizeFolder = "xlarge";
        // xlarge stays xlarge
      }

      const imageSrc = `${baseUrl}/${sizeFolder}/${imageTag}.webp`;
      return { sizeFolder, imageSrc, highDpiAdjustment, primarySize, isEstimated: false };
    }

    // Fallback to intelligent initial size
    const fallbackSize = getIntelligentInitialSize();
    return { 
      sizeFolder: fallbackSize, 
      imageSrc: `${baseUrl}/${fallbackSize}/${imageTag}.webp`,
      highDpiAdjustment: false,
      primarySize: fallbackSize,
      isEstimated: true
    };
  }, [screenSize, effectiveWidth, baseUrl, imageTag]);
};

export const useResponsiveProps = (
  fill: boolean | undefined,
  width: number | undefined,
  height: number | undefined
) => {
  return useMemo(() => {
    return fill
      ? {
          fill: true,
          sizes:
            "(min-width: 1920px) 1920px, (min-width: 1280px) 1280px, (min-width: 1024px) 1024px, (min-width: 640px) 640px, 320px",
        }
      : {
          width,
          height,
          sizes: width
            ? `(min-width: 1920px) ${Math.min(
                width,
                1920
              )}px, (min-width: 1280px) ${Math.min(
                width,
                1280
              )}px, (min-width: 1024px) ${Math.min(
                width,
                1024
              )}px, (min-width: 640px) ${Math.min(width, 640)}px, ${width}px`
            : "(min-width: 1920px) 1920px, (min-width: 1280px) 1280px, (min-width: 1024px) 1024px, (min-width: 640px) 640px, 320px",
        };
  }, [fill, width, height]);
};

export const getImageStyle = (
  fill: boolean | undefined,
  width: number | undefined,
  height: number | undefined,
  customStyle?: React.CSSProperties
): React.CSSProperties => {
  return {
    width: fill ? "100%" : width,
    height: fill ? "100%" : height,
    objectFit: fill ? "cover" : "contain",
    ...customStyle,
  };
};
